import { createSlice } from '@reduxjs/toolkit';

import { fetchExampleList, renameScenario, saveScenario } from 'store/reducers/thunks/scenarioThunks';
import { fetchPlans } from 'store/reducers/thunks/planThunks';
import { loadRunEmphasis } from 'store/functions/runEmphasis';

// Run lifecycle statuses.
const TERMINAL_RUN_STATUSES = ['succeeded', 'failed', 'cancelled'];
const ACTIVE_RUN_STATUSES = ['queued', 'running'];

// How many consecutive failed /run_status polls a run survives before we stop
// polling it. Without a cap, a run whose status request keeps erroring (expired
// auth after an overnight tab, network loss, a deleted run) stays 'running'
// forever and the poller — plus every re-render its dispatches trigger — never
// stops. At the poller's 5s fallback interval this is ~2 minutes of grace, so
// a laptop-sleep blip (which fails once or twice, then recovers and resets the
// counter below) never trips it. Exported for tests.
export const MAX_STATUS_FETCH_FAILURES = 24;
// Cap the tracker so a long session doesn't grow the list unbounded.
const MAX_TRACKED_JOBS = 25;

// Client-side cap on concurrent (queued+running) solves, so we don't flood the
// backend before its authoritative per-plan limit (429) even responds. Signed-out
// users get a stricter cap. Shared by the Optimize button and the runs indicator.
export const MAX_ACTIVE_SOLVES_SIGNED_OUT = 1;
export const MAX_ACTIVE_SOLVES_SIGNED_IN = 2;

const findJob = (state, runId) => state.solveJobs.find((j) => j.runId === runId);
const findJobById = (state, id) => state.solveJobs.find((j) => j.id === id);
// Max runs a session can have queued + in flight at once (client-side guard on the
// queue length, separate from the per-slot concurrency cap above).
export const MAX_QUEUED_RUNS = 12;

/**
 * UI Reducer
 *
 * Redux slice for managing UI-related state across the application.
 *
 * This reducer handles the user interface state, specifically managing
 * the sidebar's minimized/expanded state.
 */

const SIDEBAR_MINI_KEY = 'ui.sidebarMini';
const SIDEBAR_MINI_USER_SET_KEY = 'ui.sidebarMini.userSet';

const readSessionBoolean = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.sessionStorage.getItem(key);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  } catch (error) {
    return null;
  }
};

const isHomePath = (pathname) => pathname === '/home' || pathname.startsWith('/home/') || pathname === '/index' || pathname.startsWith('/index/');

const getInitialSidebarMini = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userSet = readSessionBoolean(SIDEBAR_MINI_USER_SET_KEY);
  const stored = readSessionBoolean(SIDEBAR_MINI_KEY);

  // If the user already chose a preference this session, use it.
  if (userSet === true && stored !== null) {
    return stored;
  }

  // Otherwise, default based on the initial entry route.
  const pathname = window.location?.pathname || '';
  return isHomePath(pathname);
};

const getInitialSidebarMiniUserSet = () => readSessionBoolean(SIDEBAR_MINI_USER_SET_KEY) === true;

const persistSidebarMiniUserPreference = (sidebarMini) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SIDEBAR_MINI_KEY, String(sidebarMini));
    window.sessionStorage.setItem(SIDEBAR_MINI_USER_SET_KEY, 'true');
  } catch (error) {
    // Ignore sessionStorage failures (e.g. in private mode / restricted environments).
  }
};

const REMEMBERED_SWITCHES_KEY = 'ui.rememberedSwitches';

// Declared switches and their factory defaults. Only these keys are ever read
// back from storage or accepted by the reducers, so a stale or corrupt
// localStorage entry can never introduce unknown switches into state.
// Exported and frozen so tests can assert against the real list rather than a
// hand-copied one. The copy in the test file drifted the first time a switch
// was added after it was written, which is the failure this prevents. Frozen
// because every read spreads it — nothing should ever mutate the defaults.
export const REMEMBERED_SWITCH_DEFAULTS = Object.freeze({
  ignoreExitExampleModeWarning: false,
  rememberDownloadFormat: false,
  // The optimizer empty-state front door, collapsed by "Skip setup". A person
  // who dismissed it once shouldn't meet it expanded on every empty scenario.
  frontDoorCollapsed: false,
  // The "Added <place> as location N" toast. Useful confirmation on the first
  // few clicks; noise by the thirtieth, since it fires on every single one.
  // Set by "Don't show again" on the toast itself.
  ignoreLocationAddedToast: false,
  // Account-page cards, folded by default. Both hold lists that grow without
  // limit — 68 saved rows and every scenario ever kept — so left open they bury
  // the account details, the plan and the usage stats under two scrolling
  // inventories nobody came to read. Each card's header still carries its
  // count, so what is inside is legible without opening it.
  //
  // The choice outlasts the visit either way: expand one and it stays expanded.
  accountLibraryCollapsed: true,
  accountScenariosCollapsed: true,
  // The one-time "Saved as a new scenario" notice after the scenario-identity
  // update (store/functions/scenarioIdentity.js). Set when it is shown, never
  // by the user, so it is only ever shown once in this browser.
  savedAsNewAfterUpdateNoticeShown: false,
});

// These are user-facing "saved preferences", so unlike the rest of the `ui`
// slice (which redux-persist deliberately drops) they must survive a reload.
// The slice isn't persisted, so we mirror just these switches to localStorage
// on write and rehydrate them here on boot — the same approach the sidebar
// preference uses, but localStorage rather than sessionStorage so the choice
// carries across browser sessions, not just the current tab.
const getInitialRememberedSwitches = () => {
  const base = { ...REMEMBERED_SWITCH_DEFAULTS };
  if (typeof window === 'undefined') return base;

  try {
    const raw = window.localStorage.getItem(REMEMBERED_SWITCHES_KEY);
    if (!raw) return base;
    const stored = JSON.parse(raw);
    if (stored && typeof stored === 'object') {
      // Merge only known keys, and only genuine booleans, over the defaults.
      for (const key of Object.keys(base)) {
        if (typeof stored[key] === 'boolean') base[key] = stored[key];
      }
    }
  } catch (error) {
    // Unreadable / unparseable value (private mode, corruption) — defaults stand.
  }
  return base;
};

const persistRememberedSwitches = (switches) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REMEMBERED_SWITCHES_KEY, JSON.stringify(switches));
  } catch (error) {
    // Ignore localStorage failures (e.g. private mode / restricted environments).
  }
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    /*
    Whether the Run button still needs to stand out (store/functions/runEmphasis).
    Read once from localStorage and written back by OptimizeButton; not part of
    any persisted redux state.
    */
    runEmphasis: loadRunEmphasis(),
    /*
    Saves and renames of a scenario still waiting on the server. While one is out,
    loading another scenario is paused (the load buttons are disabled): the response
    belongs to the scenario that was open when it was sent. The reducers already
    refuse to apply a response to a different scenario (scenarioIdentity.js); this
    keeps people from reaching that state at all.
    */
    scenarioWritesInFlight: 0,
    sidebarMini: getInitialSidebarMini(),
    sidebarMiniUserSet: getInitialSidebarMiniUserSet(),
    rememberedSwitches: getInitialRememberedSwitches(),
    optimization: {
      // True while the run the user is actively awaiting is still in flight.
      pending: false,
    },
    // A point the map should bring into view, set when a stop is added from the
    // search box — the one entry path that names a place the map may not be
    // looking at (a click is where you clicked; an upload already refits).
    // Ephemeral and single-use: the map clears it once acted on, and the seq
    // counter makes two searches for the SAME address register as two requests.
    mapFocusRequest: null,
    // Session run tracker. Solves are asynchronous: submitting queues a run and
    // returns a run_id, polled until it reaches a terminal status. Every run
    // started this session is tracked here (most-recent first) so the user can
    // see status and re-open results. Transient UI — kept out of undoable state.
    // Each: { runId, scenarioName, modelType, status, submittedAt, error, resultsViewed }
    //   status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'
    //   resultsViewed: true once the user has opened this run's results (drives the
    //   "new results available" indicator on the runs pill).
    solveJobs: [],
    // The run the user is actively waiting on — its results auto-open on success.
    // (Other runs finishing in the background just update status in the tracker.)
    awaitedRunId: null,
    // Currently focused vehicle route. When non-null, the map dims all
    // other routes/stops, and result-view UIs (cards, table) can
    // visually highlight the focused vehicle. Cleared on results
    // change / model switch (see extraReducers below).
    focusedVehicleId: null,
    // Post-solve result editing. The pending (uncommitted) edit lives here —
    // NOT in the undoable input state — so previewing an edit never pollutes
    // undo history; only the commit (inputData/applyResultAmendment) does.
    // pendingAmendment: { type, label?, ...entity/position payload } | null
    resultEditing: {
      editMode: false,
      pendingAmendment: null,
    },
    // Published example scenarios (cards), fetched from /list_examples.
    // Catalog data — deliberately kept out of the undoable input state.
    // status: 'idle' | 'loading' | 'succeeded' | 'failed'
    exampleCatalog: {
      items: [],
      status: 'idle',
      error: null,
    },
    // Public plan catalog, fetched from /plans. Each plan carries a `features`
    // entitlement map that drives premium feature gating. Kept out of the
    // undoable input state. Gating fails open until this is 'succeeded', so a
    // failed/absent endpoint simply leaves every feature unlocked.
    // status: 'idle' | 'loading' | 'succeeded' | 'failed'
    // Set by inputData/setUserPlan; see the extraReducers note.
    userPlanLoaded: false,
    /*
    How many travel-cost calculations are in flight.

    Kept here, not in inputData, because inputData is undoable: the same flag
    there was part of every history entry, so an Undo could restore a "still
    calculating" state after the calculation had finished and leave Run disabled
    until a reload. Redux-undo has no idea this is transport state.
    */
    costMatrixInFlight: 0,
    planCatalog: {
      items: [],
      status: 'idle',
      error: null,
    },
  },
  reducers: {
    // The one-time attention pulse on the Run button has been shown (ever, in this browser).
    markRunPulseShown: (state) => {
      state.runEmphasis.pulseShown = true;
    },
    /* Ask the map to bring a point into view. The map decides whether it
       actually needs to move — a stop already on screen must not yank the frame
       the person chose (see components/map/functions/mapFocus.js). */
    requestMapFocus: (state, action) => {
      const { latitude, longitude } = action.payload || {};
      const seq = (state.mapFocusRequest?.seq || 0) + 1;
      state.mapFocusRequest = { latitude, longitude, seq };
    },
    clearMapFocusRequest: (state) => {
      state.mapFocusRequest = null;
    },
    /* Ask the map's address search box for focus. The locations table is where
       someone with a list of stops starts, and it has no column that takes a
       place — so without a route out of it the table reads as "this product
       needs coordinates". Same seq shape as mapFocusRequest: a counter rather
       than a boolean, so asking twice in a row still lands. */
    requestLocationSearchFocus: (state) => {
      const seq = (state.locationSearchFocusRequest?.seq || 0) + 1;
      state.locationSearchFocusRequest = { seq };
    },
    clearLocationSearchFocusRequest: (state) => {
      state.locationSearchFocusRequest = null;
    },
    // Programmatic/automatic state updates (do not mark as user preference).
    setSidebarMini: (state, action) => {
      state.sidebarMini = action.payload;
    },
    toggleSidebarMini: (state) => {
      state.sidebarMini = !state.sidebarMini;
    },

    // User intent: persist and prevent route-based defaults from overriding.
    setSidebarMiniUserPreference: (state, action) => {
      state.sidebarMini = action.payload;
      state.sidebarMiniUserSet = true;
      persistSidebarMiniUserPreference(state.sidebarMini);
    },
    toggleSidebarMiniUserPreference: (state) => {
      state.sidebarMini = !state.sidebarMini;
      state.sidebarMiniUserSet = true;
      persistSidebarMiniUserPreference(state.sidebarMini);
    },

    // Remembered switches
    /**
     * Set a remembered UI switch to an explicit boolean value.
     *
     * Payload:
     *   {
     *     key: string,   // must be a known rememberedSwitches key
     *     value: boolean
     *   }
     */
    setRememberedSwitch: (state, action) => {
      const payload = action.payload || {};
      const { key, value } = payload;

      if (typeof key !== 'string' || key.trim() === '') return;
      if (typeof value !== 'boolean') return;

      if (!state.rememberedSwitches) state.rememberedSwitches = {};

      // Only allow toggling known/declared switches.
      if (!Object.prototype.hasOwnProperty.call(state.rememberedSwitches, key)) return;

      state.rememberedSwitches[key] = value;
      persistRememberedSwitches(state.rememberedSwitches);
    },

    /**
     * Toggle a remembered UI switch.
     *
     * Payload:
     *   string  // the rememberedSwitches key to toggle
     */
    toggleRememberedSwitch: (state, action) => {
      const key = action.payload;
      if (!key) return;

      if (!state.rememberedSwitches) state.rememberedSwitches = {};
      state.rememberedSwitches[key] = !Boolean(state.rememberedSwitches[key]);
      persistRememberedSwitches(state.rememberedSwitches);
    },

    // Route focus (UI-only; not persisted into scenario state)
    setResultEditMode: (state, action) => {
      state.resultEditing.editMode = Boolean(action.payload);
      if (!state.resultEditing.editMode) state.resultEditing.pendingAmendment = null;
    },
    setPendingAmendment: (state, action) => {
      state.resultEditing.pendingAmendment = action.payload ?? null;
    },
    setFocusedVehicleId: (state, action) => {
      // Accept null/undefined or string-coercible values. Storing as a
      // string keeps comparisons consistent with feature properties,
      // which arrive from Mapbox as strings.
      const v = action.payload;
      state.focusedVehicleId = v == null ? null : String(v);
    },
    clearFocusedVehicleId: (state) => {
      state.focusedVehicleId = null;
    },

    /*
    A scenario write is starting / has ended, counted around the WHOLE flow
    rather than around the request alone.

    saveScenario.pending is too late for "Save New Scenario": it checks the save
    limit first (/can_user_save), and while that round trip was out nothing had
    been dispatched yet, so the counter was still 0 and the OTHER Save button was
    enabled. Pressing Save New then Save produced two /save_instance requests and
    two success notices. saveScenarioFlow raises the counter before the limit
    check and lowers it in its finally, which covers every branch.

    A counter, not a flag: the save itself raises it again (below), so the two
    nest.
    */
    scenarioWriteStarted: (state) => {
      state.scenarioWritesInFlight = (state.scenarioWritesInFlight || 0) + 1;
    },
    scenarioWriteEnded: (state) => {
      state.scenarioWritesInFlight = Math.max(0, (state.scenarioWritesInFlight || 0) - 1);
    },

    // Optimization state
    setOptimizationPending: (state, action) => {
      if (typeof action.payload !== 'boolean') {
        throw new Error('setOptimizationPending payload must be a boolean; received: ' + typeof action.payload);
      } 
      state.optimization.pending = action.payload;
    },
  },
  extraReducers: (builder) => {
    const writeStarted = (state) => {
      state.scenarioWritesInFlight = (state.scenarioWritesInFlight || 0) + 1;
    };
    const writeEnded = (state) => {
      state.scenarioWritesInFlight = Math.max(0, (state.scenarioWritesInFlight || 0) - 1);
    };
    builder.addCase(saveScenario.pending, writeStarted);
    builder.addCase(saveScenario.rejected, writeEnded);
    builder.addCase(renameScenario.pending, writeStarted);
    builder.addCase(renameScenario.fulfilled, writeEnded);
    builder.addCase(renameScenario.rejected, writeEnded);

    // The one-time post-update save notice has been shown; never show it again here.
    builder.addCase(saveScenario.fulfilled, (state, action) => {
      writeEnded(state);
      if (!action.payload?.savedAsNewAfterUpdate) return;
      if (!state.rememberedSwitches) state.rememberedSwitches = {};
      state.rememberedSwitches.savedAsNewAfterUpdateNoticeShown = true;
      persistRememberedSwitches(state.rememberedSwitches);
    });

    // Result editing: a committed or reverted edit consumes the pending
    // preview; fresh results end the editing session entirely.
    builder.addCase('inputData/applyResultAmendment', (state) => {
      state.resultEditing.pendingAmendment = null;
    });
    builder.addCase('inputData/revertResultAmendments', (state) => {
      state.resultEditing.pendingAmendment = null;
    });

    // Enqueue: add the run to the client queue as a local "queued" job (no run_id
    // yet). The queue controller submits it (via solveOptimization) once a backend
    // slot frees. Runs are stored newest-first; the controller submits oldest-first.
    builder.addCase('posts/enqueueOptimization/fulfilled', (state, action) => {
      const id = action.payload?.id;
      if (!id) return;
      // A run started: the Run button has done its job (see store/functions/runEmphasis).
      state.runEmphasis.everRun = true;
      const ranExample = action.payload.exampleSlug;
      if (ranExample && !state.runEmphasis.examplesRun.includes(ranExample)) {
        state.runEmphasis.examplesRun.push(ranExample);
      }
      state.solveJobs.unshift({
        id,
        runId: null,
        scenarioName: action.payload.scenarioName || 'Untitled scenario',
        modelType: action.payload.modelType || null,
        status: 'queued',
        submitting: false,
        submittedAt: Date.now(),
        nextRetryAt: 0, // when > now, the controller holds off resubmitting (429 backoff)
        error: null,
        resultsViewed: false,
      });
      if (state.solveJobs.length > MAX_TRACKED_JOBS) {
        state.solveJobs = state.solveJobs.slice(0, MAX_TRACKED_JOBS);
      }
      // A new run is coming — any focused vehicle from a prior result is stale.
      state.focusedVehicleId = null;
    });

    // Submit starting: flag the job so the controller won't pick it again.
    builder.addCase('posts/solveOptimization/pending', (state, action) => {
      const job = findJobById(state, action.meta?.arg?.id);
      if (job) job.submitting = true;
    });
    // Submit accepted: the backend assigned a run_id — poll it from here.
    builder.addCase('posts/solveOptimization/fulfilled', (state, action) => {
      const job = findJobById(state, action.payload?.id);
      if (!job) return;
      job.submitting = false;
      if (action.payload?.requeued) {
        // Backend was at capacity (429) — keep the run queued and hold off
        // resubmitting until the backoff passes, rather than marking it failed.
        job.status = 'queued';
        job.runId = null;
        job.nextRetryAt = Date.now() + (action.payload.retryAfterMs || 0);
        return;
      }
      const runId = action.payload?.run_id;
      if (!runId) {
        job.status = 'failed';
        job.error = 'Could not start the run';
        return;
      }
      job.runId = runId;
      job.status = action.payload.status || 'queued';
      // Auto-open the first run the user is waiting on; later completions surface
      // via the "new results" pulse instead of yanking the view around.
      if (!state.awaitedRunId) {
        state.awaitedRunId = runId;
        state.optimization.pending = true;
      }
    });
    // Submit failed: mark the job failed (the controller respects the cap, so this
    // is a genuine failure, not the old rapid-click race).
    builder.addCase('posts/solveOptimization/rejected', (state, action) => {
      const job = findJobById(state, action.meta?.arg?.id);
      if (job) {
        job.submitting = false;
        job.status = 'failed';
        job.error = action.error?.message || 'Could not start the run';
      }
    });

    // Dequeue: drop a still-queued run before it's submitted.
    builder.addCase('posts/dequeueRun/fulfilled', (state, action) => {
      const id = action.meta?.arg?.id;
      state.solveJobs = state.solveJobs.filter((j) => j.id !== id);
    });

    // Poll result: advance the run's status in the tracker.
    builder.addCase('posts/fetchRunStatus/fulfilled', (state, action) => {
      const payload = action.payload || {};
      const job = findJob(state, payload.run_id);
      if (!job) return;
      // A successful poll ends any failure streak (see the rejected case below).
      // Guarded so the common all-healthy path stays a pure no-op for immer.
      if (job.statusFetchFailures) job.statusFetchFailures = 0;
      // No-op when nothing changed. A long-running solve is polled every few
      // seconds; if we mutated the job each time, immer would produce a new state
      // on every poll and re-render subscribers for no reason. Bail unless the
      // status/error actually changed, so only real transitions cause a render.
      const nextError = payload.error || null;
      if (job.status === payload.status && job.error === nextError) return;
      job.status = payload.status;
      job.error = nextError;
      if (payload.run_id === state.awaitedRunId && TERMINAL_RUN_STATUSES.includes(payload.status)) {
        // The awaited solve is over. Clear pending. On success we keep
        // awaitedRunId so the poller auto-opens its results; on failure/cancel
        // there's nothing to open.
        state.optimization.pending = false;
        if (payload.status !== 'succeeded') {
          state.awaitedRunId = null;
        }
      }
    });

    // Poll failure: count consecutive misses and give up on the run once the
    // cap is hit. There was previously no rejected handler at all, so a run
    // whose /run_status request kept throwing stayed 'queued'/'running' forever
    // — an immortal 5s poll loop that churned memory for as long as the tab
    // lived. Marking the job failed takes it out of ACTIVE_RUN_STATUSES, which
    // is what actually stops the poller.
    builder.addCase('posts/fetchRunStatus/rejected', (state, action) => {
      const job = findJob(state, action.meta?.arg?.runId);
      if (!job || !ACTIVE_RUN_STATUSES.includes(job.status)) return;
      job.statusFetchFailures = (job.statusFetchFailures || 0) + 1;
      if (job.statusFetchFailures < MAX_STATUS_FETCH_FAILURES) return;
      job.status = 'failed';
      job.error = 'Lost contact with this run — its status could not be fetched.';
      if (job.runId === state.awaitedRunId) {
        state.optimization.pending = false;
        state.awaitedRunId = null;
      }
    });

    // Cancel: optimistically mark the run cancelled, then confirm.
    builder.addCase('posts/cancelRun/pending', (state, action) => {
      const runId = action.meta?.arg?.runId;
      const job = findJob(state, runId);
      if (job && ACTIVE_RUN_STATUSES.includes(job.status)) job.status = 'cancelled';
      if (runId === state.awaitedRunId) {
        state.optimization.pending = false;
        state.awaitedRunId = null;
      }
    });
    builder.addCase('posts/cancelRun/fulfilled', (state, action) => {
      const job = findJob(state, action.payload?.run_id);
      if (job && action.payload?.status) job.status = action.payload.status;
    });

    // Results loaded — mark the run's results as seen (clears the "new results"
    // indicator), and if this was the awaited run's auto-open, we're done with it.
    builder.addCase('posts/fetchRunResults/fulfilled', (state, action) => {
      const runId = action.meta?.arg?.runId;
      const job = findJob(state, runId);
      if (job) job.resultsViewed = true;
      if (runId && runId === state.awaitedRunId) state.awaitedRunId = null;
      // Fresh results replace whatever was being edited — end the session.
      state.resultEditing.editMode = false;
      state.resultEditing.pendingAmendment = null;
    });
    builder.addCase('posts/fetchRunResults/rejected', (state, action) => {
      const runId = action.meta?.arg?.runId;
      if (runId && runId === state.awaitedRunId) {
        state.awaitedRunId = null;
        state.optimization.pending = false;
      }
      // Reflect the load failure in the tracker so the run doesn't look openable.
      const job = findJob(state, runId);
      if (job && job.status === 'succeeded') job.error = 'Could not load results';
    });

    // Example catalog (/list_examples)
    builder.addCase(fetchExampleList.pending, (state) => {
      state.exampleCatalog.status = 'loading';
      state.exampleCatalog.error = null;
    });
    builder.addCase(fetchExampleList.fulfilled, (state, action) => {
      state.exampleCatalog.items = action.payload;
      state.exampleCatalog.status = 'succeeded';
      state.exampleCatalog.error = null;
    });
    builder.addCase(fetchExampleList.rejected, (state, action) => {
      state.exampleCatalog.status = 'failed';
      state.exampleCatalog.error = action.error?.message || 'Failed to load examples';
    });

    // Plan catalog (/plans) — drives feature gating. A failed load is expected
    // where the endpoint isn't deployed yet; gating fails open on any non-
    // succeeded status, so this never locks a feature by mistake.
    builder.addCase(fetchPlans.pending, (state) => {
      state.planCatalog.status = 'loading';
      state.planCatalog.error = null;
    });
    builder.addCase(fetchPlans.fulfilled, (state, action) => {
      state.planCatalog.items = action.payload;
      state.planCatalog.status = 'succeeded';
      state.planCatalog.error = null;
    });
    builder.addCase(fetchPlans.rejected, (state, action) => {
      state.planCatalog.status = 'failed';
      state.planCatalog.error = action.error?.message || 'Failed to load plans';
    });

    // Whether this session has heard back about the signed-in user's plan. The
    // login reducer writes plan 'free' the moment someone signs in, before
    // /user_plan answers, so until then 'free' is a placeholder, not a fact.
    // See selectRoadNetworkAccess. Not persisted (the ui slice never is), so a
    // reload waits for LoginSync to load the plan again.
    builder.addCase('inputData/login', (state) => {
      state.userPlanLoaded = false;
    });
    builder.addCase('inputData/logoutUser', (state) => {
      state.userPlanLoaded = false;
    });
    builder.addCase('inputData/setUserPlan', (state) => {
      state.userPlanLoaded = true;
    });

    // Matched by action type rather than by importing the thunk: costMatrixThunks
    // imports the input reducer, and this slice is imported from there.
    builder.addCase('posts/fetchTravelCosts/pending', (state) => {
      state.costMatrixInFlight += 1;
    });
    builder.addCase('posts/fetchTravelCosts/fulfilled', (state) => {
      state.costMatrixInFlight = Math.max(0, state.costMatrixInFlight - 1);
    });
    builder.addCase('posts/fetchTravelCosts/rejected', (state) => {
      state.costMatrixInFlight = Math.max(0, state.costMatrixInFlight - 1);
    });
  }
});

export const {
  setSidebarMini,
  toggleSidebarMini,
  setSidebarMiniUserPreference,
  toggleSidebarMiniUserPreference,
  setRememberedSwitch,
  toggleRememberedSwitch,
  setOptimizationPending,
  setFocusedVehicleId,
  clearFocusedVehicleId,
  setResultEditMode,
  setPendingAmendment,
  requestMapFocus,
  clearMapFocusRequest,
  requestLocationSearchFocus,
  clearLocationSearchFocusRequest,
  markRunPulseShown,
  scenarioWriteStarted,
  scenarioWriteEnded,
} = uiSlice.actions;

export const selectRunEmphasis = (state) => state.ui.runEmphasis;

export const selectMapFocusRequest = (state) => state.ui.mapFocusRequest;
export const selectLocationSearchFocusRequest = (state) => state.ui.locationSearchFocusRequest;
export const selectSidebarMini = (state) => state.ui.sidebarMini;
export const selectSidebarMiniUserSet = (state) => state.ui.sidebarMiniUserSet;
export const selectRememberedSwitch = (key) => (state) => {
  if (!state.ui.rememberedSwitches) return undefined;
  return state.ui.rememberedSwitches[key];
};
export const selectOptimizationPending = (state) => state.ui.optimization.pending;
export const selectSolveJobs = (state) => state.ui.solveJobs;
export const selectAwaitedRunId = (state) => state.ui.awaitedRunId;
export const selectActiveSolveCount = (state) =>
  state.ui.solveJobs.filter((j) => ACTIVE_RUN_STATUSES.includes(j.status)).length;
// True when a run has succeeded but the user hasn't opened its results yet —
// drives the "new results available" pulse on the runs pill.
export const selectHasNewResults = (state) =>
  state.ui.solveJobs.some((j) => j.status === 'succeeded' && !j.error && !j.resultsViewed);
// Runs currently executing on the backend.
/** True while a travel-cost calculation is running. Survives Undo (see the ui state). */
export const selectCostMatrixInFlight = (state) => (state.ui.costMatrixInFlight || 0) > 0;

export const selectRunningCount = (state) =>
  state.ui.solveJobs.filter((j) => j.status === 'running').length;
// Runs waiting for a slot (both client-queued and backend-queued).
export const selectQueuedCount = (state) =>
  state.ui.solveJobs.filter((j) => j.status === 'queued').length;

/*
Cap-aware display status for the run list. The backend briefly reports a run as
`queued` between accepting it and a worker picking it up, so a run that isn't
actually waiting behind others would flash "Queued" even when a slot was free.
Given the concurrency `cap`, the oldest `cap` non-terminal runs (FIFO — the queue
controller submits oldest first) are really starting/running; only runs beyond the
cap are genuinely queued. Returns a map of job id -> display status.
*/
export function runDisplayStatuses(jobs, cap) {
  const byId = {};
  let activeUsed = 0;
  for (let i = jobs.length - 1; i >= 0; i -= 1) {
    const job = jobs[i];
    if (TERMINAL_RUN_STATUSES.includes(job.status)) {
      byId[job.id] = job.status;
    } else if (activeUsed < cap) {
      activeUsed += 1;
      byId[job.id] = job.status === 'running' ? 'running' : 'starting';
    } else {
      byId[job.id] = 'queued';
    }
  }
  return byId;
}

export const selectFocusedVehicleId = (state) => state.ui.focusedVehicleId ?? null;

export const selectResultEditMode = (state) => state.ui.resultEditing?.editMode ?? false;
export const selectPendingAmendment = (state) => state.ui.resultEditing?.pendingAmendment ?? null;

// Example catalog selectors
export const selectExampleCatalog = (state) => state.ui.exampleCatalog;
export const selectExampleList = (state) => state.ui.exampleCatalog.items;
export const selectExampleCatalogStatus = (state) => state.ui.exampleCatalog.status;
export const selectExampleBySlug = (slug) => (state) =>
  state.ui.exampleCatalog.items.find((ex) => ex.slug === slug) ?? null;

// Plan catalog selectors (/plans)
export const selectPlanCatalog = (state) => state.ui.planCatalog;
export const selectPlanCatalogItems = (state) => state.ui.planCatalog.items;
export const selectPlanCatalogStatus = (state) => state.ui.planCatalog.status;

export default uiSlice.reducer;
